import { useStore } from '@tanstack/react-form';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useQueryUserData } from '~/data/user';

import AuthService from '~/services/auth.service';
import ToastService from '~/services/toast.service';

import { saveDraft } from './persistDraft';
import { useBugReportForm } from './useBugReportForm';
import { useBugReportFormHydration } from './useBugReportFormHydration';
import { useFileAttachment } from './useFileAttachment';
import { useScreenshotCapture } from './useScreenshotCapture';
import { getReporterName, isSystemAccount } from './utils';

type P = {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onMinimize: () => void;
  readonly screenshot: File | undefined;
  readonly setScreenshot: (f: File | undefined) => void;
};

export function useBugReport({
  isOpen,
  onClose,
  onMinimize,
  screenshot,
  setScreenshot,
}: P) {
  const { t } = useTranslation('bugReport');

  const { data: currentUser } = useQueryUserData();

  const derivedName = getReporterName({ email: currentUser?.email });

  const [harFile, setHarFile] = useState<File | undefined>();
  const [isHarInstructionsOpen, setIsHarInstructionsOpen] = useState(false);
  const [isVideoLinkVisible, setIsVideoLinkVisible] = useState(false);

  const { handleCaptureScreenshot, isCapturing } = useScreenshotCapture({
    setScreenshot,
  });
  const handleAttachFile = useFileAttachment();

  const titleInputRef = useRef<HTMLInputElement>(null);

  const isNameSystemAccount =
    !derivedName &&
    isSystemAccount({
      email: currentUser?.email,
      id: AuthService.getUserIdFromAccessToken(),
    });

  const form = useBugReportForm({
    currentUser,
    derivedName,
    harFile,
    onSubmitError() {
      ToastService.error([t(($) => $.toast.error)]);
    },
    onSubmitSuccess() {
      ToastService.success(t(($) => $.toast.success));

      setScreenshot(undefined);
      setHarFile(undefined);
      setIsVideoLinkVisible(false);

      onClose();
    },
    screenshot,
  });

  useBugReportFormHydration({
    derivedName,
    form,
    isOpen,
    setIsVideoLinkVisible,
    titleInputRef,
  });

  const isSubmitting = useStore(form.store, (state) => state.isSubmitting);
  const isBusy = isSubmitting || isCapturing;

  /** Silent close: draft is preserved. Called for X, backdrop, Esc, and cancel. */
  const handleSilentClose = () => {
    if (isBusy) {
      return;
    }

    setHarFile(undefined);
    setIsVideoLinkVisible(false);

    onClose();
  };

  /** Save draft immediately, then minimize. */
  const handleMinimize = () => {
    if (isBusy) {
      return;
    }

    // Flush ahead of the listener's debounce so the draft is guaranteed to
    // be persisted before any subsequent reload / navigation.
    saveDraft(form.store.state.values);

    onMinimize();
  };

  return {
    currentUser,
    derivedName,
    form,
    handleAttachFile,
    handleCaptureScreenshot,
    handleMinimize,
    handleSilentClose,
    harFile,
    isBusy,
    isCapturing,
    isHarInstructionsOpen,
    isNameSystemAccount,
    isSubmitting,
    isVideoLinkVisible,
    setHarFile,
    setIsHarInstructionsOpen,
    setIsVideoLinkVisible,
    titleInputRef,
  };
}
